/**
 * LifeScribe Subscription Plans — Frontend Single Source of Truth
 * ================================================================
 *
 * Mirrors the `subscription_plan_map` DB table (migration 321). Values below are
 * used as the fallback when `GET /api/adminUserManagement/subscription-plans`
 * is unreachable. On app boot, prefer the API response and cache it.
 *
 * Plan codes match `users.subscription_plan` exactly.
 */

export type SubscriptionTier = 'free' | 'trial' | 'standard' | 'premium' | 'legacy_creator';
export type BillingCycle = 'monthly' | 'annual';
export type VaultTier = 'standard' | 'premium';

export interface SubscriptionPlan {
  plan_code: string;
  display_name: string;
  display_tier: string;
  monthly_price: number | null;
  annual_price: number | null;
  billing_cycle: 'month' | 'year' | 'one_time';
  tier_order: number;
  is_active: boolean;
  is_legacy: boolean;
  feature_summary: string | null;
}

// Core tier options for the Create Member segmented control
export const SUBSCRIPTION_TIERS: { value: SubscriptionTier; label: string; hasCycle: boolean }[] = [
  { value: 'free', label: 'Free', hasCycle: false },
  { value: 'trial', label: 'Trial', hasCycle: false },
  { value: 'standard', label: 'Standard', hasCycle: true },
  { value: 'premium', label: 'Premium', hasCycle: true },
  { value: 'legacy_creator', label: 'Legacy Creator', hasCycle: true },
];

export const BILLING_CYCLES: { value: BillingCycle; label: string }[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual' },
];

export const VAULT_TIERS: { value: VaultTier | 'none'; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'standard', label: 'Standard — 100 GB, 7 members' },
  { value: 'premium', label: 'Premium — 250 GB, 20 members' },
];

/**
 * Static plan list (fallback if API fails). Order matches `tier_order`.
 * Prices are USD.
 */
export const FALLBACK_SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  { plan_code: 'free', display_name: 'Free', display_tier: 'free', monthly_price: 0, annual_price: null, billing_cycle: 'month', tier_order: -1, is_active: true, is_legacy: false, feature_summary: 'Free tier' },
  { plan_code: 'trial', display_name: 'Free Trial', display_tier: 'trial', monthly_price: 0, annual_price: null, billing_cycle: 'month', tier_order: 0, is_active: true, is_legacy: false, feature_summary: '7-day free trial' },
  { plan_code: 'standard_monthly', display_name: 'Standard Monthly', display_tier: 'standard', monthly_price: 9.99, annual_price: null, billing_cycle: 'month', tier_order: 1, is_active: true, is_legacy: false, feature_summary: '15 chapters/mo, 2 AI images' },
  { plan_code: 'standard_annual', display_name: 'Standard Annual', display_tier: 'standard', monthly_price: null, annual_price: 99.99, billing_cycle: 'year', tier_order: 2, is_active: true, is_legacy: false, feature_summary: '15 chapters/mo, 2 AI images, save 17%' },
  { plan_code: 'premium_monthly', display_name: 'Premium Monthly', display_tier: 'premium', monthly_price: 19.99, annual_price: null, billing_cycle: 'month', tier_order: 3, is_active: true, is_legacy: false, feature_summary: '15 chapters/mo, voice cloning, advanced AI' },
  { plan_code: 'premium_annual', display_name: 'Premium Annual', display_tier: 'premium', monthly_price: null, annual_price: 149.99, billing_cycle: 'year', tier_order: 4, is_active: true, is_legacy: false, feature_summary: '15 chapters/mo, voice cloning, advanced AI, save 38%' },
  { plan_code: 'legacy_monthly', display_name: 'Legacy Creator Monthly', display_tier: 'legacy_creator', monthly_price: 29.99, annual_price: null, billing_cycle: 'month', tier_order: 5, is_active: true, is_legacy: false, feature_summary: '15 chapters/mo, all features + priority AI + Memory Videos' },
  { plan_code: 'legacy_annual', display_name: 'Legacy Creator Annual', display_tier: 'legacy_creator', monthly_price: null, annual_price: 239.99, billing_cycle: 'year', tier_order: 6, is_active: true, is_legacy: false, feature_summary: '15 chapters/mo, all features + AI Memory Videos, save 33%' },
  { plan_code: 'vault_standard_monthly', display_name: 'Family Vault Standard Monthly', display_tier: 'vault_standard', monthly_price: 2.99, annual_price: null, billing_cycle: 'month', tier_order: 7, is_active: true, is_legacy: false, feature_summary: '100 GB, 7 family members' },
  { plan_code: 'vault_standard_annual', display_name: 'Family Vault Standard Annual', display_tier: 'vault_standard', monthly_price: null, annual_price: 29.99, billing_cycle: 'year', tier_order: 8, is_active: true, is_legacy: false, feature_summary: '100 GB, 7 family members, save 30%+' },
  { plan_code: 'vault_premium_monthly', display_name: 'Family Vault Premium Monthly', display_tier: 'vault_premium', monthly_price: 6.99, annual_price: null, billing_cycle: 'month', tier_order: 9, is_active: true, is_legacy: false, feature_summary: '250 GB, 20 family members' },
  { plan_code: 'vault_premium_annual', display_name: 'Family Vault Premium Annual', display_tier: 'vault_premium', monthly_price: null, annual_price: 69.99, billing_cycle: 'year', tier_order: 10, is_active: true, is_legacy: false, feature_summary: '250 GB, 20 family members, save 30%+' },
];

/** Map a (tier, cycle) pair to the DB plan_code. */
export function planCodeFor(tier: SubscriptionTier, cycle?: BillingCycle): string {
  if (tier === 'free') return 'free';
  if (tier === 'trial') return 'trial';
  if (tier === 'legacy_creator') return cycle === 'monthly' ? 'legacy_monthly' : 'legacy_annual';
  return `${tier}_${cycle ?? 'annual'}`;
}

/** Map a family vault (tier, cycle) pair to the DB plan_code. */
export function vaultPlanCodeFor(tier: VaultTier, cycle: BillingCycle): string {
  return `vault_${tier}_${cycle === 'monthly' ? 'monthly' : 'annual'}`;
}

/**
 * Turn a plan_code into a human-friendly label.
 * Falls back to the raw code if not found (handles retired/unknown plans).
 */
export function displayNameFor(planCode: string | null | undefined, plans: SubscriptionPlan[] = FALLBACK_SUBSCRIPTION_PLANS): string {
  if (!planCode) return '—';
  const match = plans.find(p => p.plan_code === planCode);
  return match ? match.display_name : planCode;
}

/** Return the numeric price for a plan_code (monthly or annual, whichever is set).
 *  Postgres NUMERIC columns arrive as strings over JSON, so coerce defensively. */
export function priceFor(planCode: string, plans: SubscriptionPlan[] = FALLBACK_SUBSCRIPTION_PLANS): number | null {
  const match = plans.find(p => p.plan_code === planCode);
  if (!match) return null;
  const raw = match.annual_price ?? match.monthly_price;
  if (raw === null || raw === undefined) return null;
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
  return Number.isFinite(n) ? n : null;
}

/** Tier → color for Chip rendering (matches admin design tokens). */
export const TIER_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
  free: 'default',
  trial: 'info',
  standard: 'primary',
  premium: 'secondary',
  legacy_creator: 'warning',
  vault_standard: 'primary',
  vault_premium: 'secondary',
};
